import { useState, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import IconifyIcon from '../../components/IconifyIcon';
import apiService from '../../services/api.service';

// Type config with icons and colors - all using #dccc90 and black
const typeConfig = {
  order: { 
    icon: 'solar:bag-4-bold-duotone', 
    label: 'Commande',
    color: '#dccc90'
  },
  client: { 
    icon: 'solar:users-group-rounded-bold-duotone', 
    label: 'Client',
    color: '#dccc90'
  },
  stock: { 
    icon: 'solar:box-bold-duotone', 
    label: 'Stock',
    color: '#dccc90'
  },
  supplier: { 
    icon: 'solar:delivery-bold-duotone', 
    label: 'Fournisseur',
    color: '#dccc90'
  }
};

// Get read notifications from localStorage
const getReadNotifications = () => {
  try {
    const read = localStorage.getItem('readNotifications');
    return read ? JSON.parse(read) : [];
  } catch {
    return [];
  }
};

// Save read notification to localStorage
const markAsRead = (notificationId) => {
  try {
    const read = getReadNotifications();
    if (!read.includes(notificationId)) {
      read.push(notificationId);
      localStorage.setItem('readNotifications', JSON.stringify(read));
    }
  } catch (e) {
    console.error('Error saving read notification:', e);
  }
};

// Mark as unread
const markAsUnread = (notificationId) => {
  try {
    const read = getReadNotifications();
    const updated = read.filter(id => id !== notificationId);
    localStorage.setItem('readNotifications', JSON.stringify(updated));
  } catch (e) {
    console.error('Error marking as unread:', e);
  }
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [allNotifications, setAllNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [readIds, setReadIds] = useState(getReadNotifications());

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/notifications');
      if (response.success) {
        setAllNotifications(response.data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    let filtered = [...allNotifications];
    if (filter === 'unread') {
      filtered = filtered.filter(n => !readIds.includes(n.id));
    } else if (filter === 'read') {
      filtered = filtered.filter(n => readIds.includes(n.id));
    }
    if (typeFilter !== 'all') {
      filtered = filtered.filter(n => n.type === typeFilter);
    }
    setNotifications(filtered);
  }, [allNotifications, filter, typeFilter, readIds]);

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    setReadIds(getReadNotifications());
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleToggleRead = (e, notification) => {
    e.stopPropagation();
    const isRead = readIds.includes(notification.id);
    if (isRead) {
      markAsUnread(notification.id);
    } else {
      markAsRead(notification.id);
    }
    setReadIds(getReadNotifications());
  };

  const handleMarkAllAsRead = () => {
    allNotifications.forEach(n => markAsRead(n.id));
    setReadIds(getReadNotifications());
  };

  const unreadCount = allNotifications.filter(n => !readIds.includes(n.id)).length;

  return (
    <div className="notifications-page">
      <style>{`
        .notifications-page {
          min-height: 100vh;
          background: #f5f5f5;
          padding: 20px;
        }
        
        @media (min-width: 768px) {
          .notifications-page {
            padding: 30px;
          }
        }
        
        .notifications-header {
          background: #dccc90;
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
        }
        
        @media (min-width: 768px) {
          .notifications-header {
            border-radius: 20px;
            padding: 30px;
            margin-bottom: 25px;
          }
        }
        
        .notifications-header-title {
          color: #1a1a1a;
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 6px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        @media (min-width: 768px) {
          .notifications-header-title {
            font-size: 28px;
            gap: 12px;
          }
        }
        
        .notifications-header-title svg {
          font-size: 24px;
        }
        
        @media (min-width: 768px) {
          .notifications-header-title svg {
            font-size: 36px;
          }
        }
        
        .notifications-header-subtitle {
          color: rgba(0, 0, 0, 0.6);
          font-size: 13px;
          margin-bottom: 0;
        }
        
        @media (min-width: 768px) {
          .notifications-header-subtitle {
            font-size: 15px;
          }
        }
        
        .notifications-header-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 15px;
        }
        
        @media (min-width: 768px) {
          .notifications-header-actions {
            margin-top: 0;
            justify-content: flex-end;
          }
        }
        
        .notifications-badge {
          background: rgba(255,255,255,0.7);
          color: #1a1a1a;
          padding: 8px 14px;
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        @media (min-width: 768px) {
          .notifications-badge {
            padding: 8px 20px;
            font-size: 14px;
          }
        }
        
        .notifications-header-btn {
          background: rgba(255,255,255,0.5);
          border: 1px solid rgba(255,255,255,0.8);
          color: #1a1a1a;
          padding: 8px 14px;
          border-radius: 10px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.3s ease;
          flex: 1;
          justify-content: center;
        }
        
        @media (min-width: 768px) {
          .notifications-header-btn {
            padding: 10px 20px;
            font-size: 14px;
            flex: none;
          }
        }
        
        .notifications-header-btn:hover {
          background: rgba(255,255,255,0.7);
        }
        
        .notifications-filter-card {
          background: #fff;
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 20px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          border: none;
        }
        
        @media (min-width: 768px) {
          .notifications-filter-card {
            border-radius: 16px;
            padding: 20px 25px;
            margin-bottom: 25px;
          }
        }
        
        .notifications-filter-section {
          margin-bottom: 12px;
        }
        
        @media (min-width: 768px) {
          .notifications-filter-section {
            margin-bottom: 0;
          }
        }
        
        .notifications-filter-label {
          color: #6c757d;
          font-weight: 600;
          font-size: 12px;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        @media (min-width: 768px) {
          .notifications-filter-label {
            font-size: 14px;
            margin-bottom: 0;
            margin-right: 10px;
          }
        }
        
        .notifications-filter-btns {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .notifications-filter-btn {
          background: #f8f9fa;
          color: #6c757d;
          border: none;
          padding: 8px 12px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        @media (min-width: 768px) {
          .notifications-filter-btn {
            padding: 10px 18px;
            font-size: 13px;
            border-radius: 10px;
          }
        }
        
        .notifications-filter-btn.active {
          background: #dccc90;
          color: #1a1a1a;
        }
        
        .notifications-type-btn {
          background: transparent;
          color: #6c757d;
          border: 2px solid #e9ecef;
          padding: 6px 10px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 11px;
          font-weight: 500;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        @media (min-width: 768px) {
          .notifications-type-btn {
            padding: 8px 16px;
            font-size: 13px;
            border-radius: 10px;
          }
        }
        
        .notifications-type-btn.active {
          background: #dccc90;
          color: #1a1a1a;
          border-color: #dccc90;
        }
        
        .notification-card {
          background: #fff;
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 12px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
          border: 2px solid #dccc90;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
        }
        
        @media (min-width: 768px) {
          .notification-card {
            border-radius: 16px;
            padding: 20px;
            margin-bottom: 15px;
          }
        }
        
        .notification-card.read {
          background: #f8f9fa;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          border: 1px solid #e9ecef;
          opacity: 0.75;
        }
        
        .notification-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0,0,0,0.15);
        }
        
        .notification-icon-wrapper {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: #dccc90;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
          flex-shrink: 0;
        }
        
        @media (min-width: 768px) {
          .notification-icon-wrapper {
            width: 56px;
            height: 56px;
            border-radius: 14px;
          }
        }
        
        .notification-icon-wrapper svg {
          font-size: 20px;
          color: #1a1a1a;
        }
        
        @media (min-width: 768px) {
          .notification-icon-wrapper svg {
            font-size: 26px;
          }
        }
        
        .notification-new-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: #dccc90;
          color: #1a1a1a;
          padding: 3px 8px;
          border-radius: 20px;
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        @media (min-width: 768px) {
          .notification-new-badge {
            top: 15px;
            right: 15px;
            padding: 4px 12px;
            font-size: 11px;
          }
        }
        
        .notification-content {
          flex: 1;
          min-width: 0;
          margin-left: 12px;
        }
        
        @media (min-width: 768px) {
          .notification-content {
            margin-left: 15px;
          }
        }
        
        .notification-title {
          font-size: 14px;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 4px;
          line-height: 1.3;
        }
        
        @media (min-width: 768px) {
          .notification-title {
            font-size: 16px;
            margin-bottom: 6px;
          }
        }
        
        .notification-title.read {
          font-weight: 500;
          color: #6c757d;
        }
        
        .notification-message {
          font-size: 12px;
          color: #666;
          margin-bottom: 8px;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        @media (min-width: 768px) {
          .notification-message {
            font-size: 14px;
            margin-bottom: 10px;
            -webkit-line-clamp: 3;
          }
        }
        
        .notification-meta {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
          font-size: 10px;
          color: #888;
        }
        
        @media (min-width: 768px) {
          .notification-meta {
            gap: 15px;
            font-size: 12px;
          }
        }
        
        .notification-type-badge {
          background: #dccc90;
          color: #1a1a1a;
          padding: 3px 8px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
        }
        
        @media (min-width: 768px) {
          .notification-type-badge {
            padding: 4px 10px;
            border-radius: 6px;
            font-size: 11px;
          }
        }
        
        .notification-action-btn {
          background: transparent;
          border: none;
          color: #95a5a6;
          cursor: pointer;
          padding: 6px;
          border-radius: 6px;
          transition: all 0.3s ease;
          display: none;
        }
        
        @media (min-width: 768px) {
          .notification-action-btn {
            display: flex;
            padding: 8px;
            border-radius: 8px;
          }
        }
        
        .notification-action-btn:hover {
          background: #f1f3f4;
        }
        
        .notifications-empty {
          text-align: center;
          padding: 40px 20px;
        }
        
        @media (min-width: 768px) {
          .notifications-empty {
            padding: 60px 20px;
          }
        }
        
        .notifications-empty-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }
        
        @media (min-width: 768px) {
          .notifications-empty-icon {
            width: 120px;
            height: 120px;
            margin: 0 auto 25px;
          }
        }
        
        .notifications-empty-icon svg {
          font-size: 35px;
          color: #adb5bd;
        }
        
        @media (min-width: 768px) {
          .notifications-empty-icon svg {
            font-size: 50px;
          }
        }
        
        .notifications-loading {
          text-align: center;
          padding: 40px 20px;
        }
        
        @media (min-width: 768px) {
          .notifications-loading {
            padding: 60px 20px;
          }
        }
        
        .notifications-footer {
          text-align: center;
          margin-top: 20px;
          color: #95a5a6;
          font-size: 11px;
          padding: 0 10px;
        }
        
        @media (min-width: 768px) {
          .notifications-footer {
            margin-top: 30px;
            font-size: 13px;
          }
        }
      `}</style>
      
      {/* Header */}
      <div className="notifications-header">
        <div>
          <h1 className="notifications-header-title">
            <IconifyIcon icon="solar:bell-bing-bold-duotone" />
            Centre de Notifications
          </h1>
          <p className="notifications-header-subtitle">
            Restez informé des dernières activités
          </p>
        </div>
        <div className="notifications-header-actions">
          <div className="notifications-badge">
            <IconifyIcon icon="solar:letter-unread-bold" />
            {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
          </div>
          <button 
            className="notifications-header-btn"
            onClick={handleMarkAllAsRead}
            disabled={unreadCount === 0}
          >
            <IconifyIcon icon="bx:check-double" />
            Marquer lu
          </button>
          <button 
            className="notifications-header-btn"
            onClick={fetchNotifications}
          >
            <IconifyIcon icon="bx:refresh" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="notifications-filter-card">
        <div className="notifications-filter-section">
          <div className="notifications-filter-label">
            <IconifyIcon icon="bx:filter-alt" />
            Statut:
          </div>
          <div className="notifications-filter-btns">
            <button 
              className={`notifications-filter-btn ${filter === 'all' ? 'active' : ''}`} 
              onClick={() => setFilter('all')}
            >
              Toutes ({allNotifications.length})
            </button>
            <button 
              className={`notifications-filter-btn ${filter === 'unread' ? 'active' : ''}`} 
              onClick={() => setFilter('unread')}
            >
              <IconifyIcon icon="bx:envelope" /> Non lues ({unreadCount})
            </button>
            <button 
              className={`notifications-filter-btn ${filter === 'read' ? 'active' : ''}`} 
              onClick={() => setFilter('read')}
            >
              <IconifyIcon icon="bx:envelope-open" /> Lues
            </button>
          </div>
        </div>
        
        <div className="notifications-filter-section" style={{ marginTop: '12px' }}>
          <div className="notifications-filter-label">
            <IconifyIcon icon="bx:category" />
            Type:
          </div>
          <div className="notifications-filter-btns">
            <button 
              className={`notifications-type-btn ${typeFilter === 'all' ? 'active' : ''}`} 
              onClick={() => setTypeFilter('all')}
            >
              Tous
            </button>
            {Object.entries(typeConfig).map(([key, config]) => (
              <button 
                key={key}
                className={`notifications-type-btn ${typeFilter === key ? 'active' : ''}`}
                onClick={() => setTypeFilter(key)}
              >
                <IconifyIcon icon={config.icon} />
                {config.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      {loading ? (
        <div className="notifications-filter-card notifications-loading">
          <Spinner animation="border" style={{ color: '#dccc90', width: '40px', height: '40px' }} />
          <p style={{ marginTop: '15px', color: '#6c757d', fontSize: '14px' }}>Chargement...</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="notifications-filter-card">
          <div className="notifications-empty">
            <div className="notifications-empty-icon">
              <IconifyIcon icon="solar:bell-off-bold-duotone" />
            </div>
            <h4 style={{ color: '#495057', marginBottom: '10px', fontWeight: '600', fontSize: '16px' }}>
              Aucune notification
            </h4>
            <p style={{ color: '#6c757d', marginBottom: '0', fontSize: '13px' }}>
              {filter !== 'all' || typeFilter !== 'all' 
                ? 'Modifiez les filtres pour voir plus'
                : 'Vous êtes à jour !'}
            </p>
          </div>
        </div>
      ) : (
        <div>
          {notifications.map((notification, idx) => {
            const config = typeConfig[notification.type] || typeConfig.order;
            const isRead = readIds.includes(notification.id);
            
            return (
              <div
                key={notification.id || idx}
                className={`notification-card ${isRead ? 'read' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                {!isRead && <div className="notification-new-badge">Nouveau</div>}
                
                <div className="d-flex align-items-start">
                  <div className="notification-icon-wrapper">
                    <IconifyIcon icon={config.icon} />
                  </div>
                  <div className="notification-content">
                    <h5 className={`notification-title ${isRead ? 'read' : ''}`}>{notification.title}</h5>
                    <p className="notification-message">{notification.message}</p>
                    <div className="notification-meta">
                      <span className="notification-type-badge">{config.label}</span>
                      <span>
                        <IconifyIcon icon="bx:time-five" className="me-1" />
                        {notification.time}
                      </span>
                      {notification.link && (
                        <span style={{ color: '#1a1a1a', fontWeight: '600' }}>
                          Voir →
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    className="notification-action-btn"
                    onClick={(e) => handleToggleRead(e, notification)}
                    title={isRead ? 'Marquer non lu' : 'Marquer lu'}
                  >
                    <IconifyIcon 
                      icon={isRead ? 'bx:envelope' : 'bx:envelope-open'} 
                      style={{ fontSize: '20px' }}
                    />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="notifications-footer">
        <IconifyIcon icon="bx:info-circle" className="me-2" />
        Notifications générées automatiquement
      </div>
    </div>
  );
};

export default NotificationsPage;
